<template>
	<section>
		<el-row class="titlerow">
			<el-col :span="24">
				<h1>Willkommen zurück, {{ user.firstName }}!</h1>
			</el-col>

			<el-row>
				<el-col class="button-panel">
					<RouterLink :to="'/statistik?months=1'" class="el-button el-button--danger is-round" :title="'Neuen User anlegen'">
						Fälle-Statistik 1 Monat
					</RouterLink>
					<RouterLink :to="'/statistik?months=3'" class="el-button el-button--danger is-round" :title="'Neuen Kontakt anlegen'">
						Fälle-Statistik 3 Monate
					</RouterLink>
					<RouterLink :to="'/statistik?months=6'" class="el-button el-button--danger is-round" :title="'Neue Beschwerde anlegen'">
						Fälle-Statistik 6 Monate
					</RouterLink>
					<RouterLink :to="'/statistik?months=12'" class="el-button el-button--danger is-round" :title="'Neue Beschwerde anlegen'">
						Fälle-Statistik 12 Monate
					</RouterLink>
				</el-col>
			</el-row>
		</el-row>
	</section>

	<template v-if="isMobile">
		<DashFilter :mainfilters="mainfilters" @toggleMainFilter="toggleMainFilter" @toggleSubFilter="toggleSubFilter" />
		<section>
			<el-row class="flex flex--aligncenter">
				<el-col :span="22">
					<h2 class="no-margin">Fälle ({{ $getCountErgebnisseLabel(casesTotalCount) }})</h2>
				</el-col>
				<el-col :span="2" class="flex flex--justifyend">
					<SortIcon class="sorticon" @click="openTableSortDrawer = true" />
				</el-col>
			</el-row>

			<CasesTable
				:tableData="filteredCases"
				:loading="caseTableLoading"
				:totalCount="casesTotalCount"
				@loadNextPage="loadNextPage"
				:mobileBox="true"
				:sort="tableSort"
			/>
		</section>
		<TableSortDrawer v-model:open="openTableSortDrawer" :currentSort="tableSort" @setSort="(sort) => (tableSort = sort)" />
	</template>

	<div class="bg--white" v-else>
		<section id="dashfilter">
			<DashFilter :mainfilters="mainfilters" @toggleMainFilter="toggleMainFilter" @toggleSubFilter="toggleSubFilter" />

			<h2>Fälle ({{ $getCountErgebnisseLabel(casesTotalCount) }})</h2>
			<CasesTable :tableData="filteredCases" :loading="caseTableLoading" :totalCount="casesTotalCount" @loadNextPage="loadNextPage" />
		</section>
	</div>
</template>

<script lang="ts">
import { searchCases } from '@/requests/searches';

import TheTodoList from '@/components/TheTodoList.vue';
import { Plus } from '@element-plus/icons-vue';
import { defineComponent, computed } from 'vue';
import DashFilter, { FilterItem } from '../components/DashFilter.vue';
import CasesTable from '../components/tables/CasesTable.vue';
import ComplaintsTable from '../components/tables/ComplaintsTable.vue';
import LastActivitiesTable from '../components/tables/LastActivitiesTable.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheTable from '../components/TheTable.vue';
import { authUser, useStore } from '../store';
import { ActivityResponse, CaseFilters, CaseResponse, CaseState, ComplaintResponse, SearchCaseRequest, TableSort } from '../types/types';
import { texts } from '../utils/texts';
import SortIcon from '../assets/icons/SortIcon.vue';
import TableSortDrawer from '../components/TableSortDrawer.vue';

export default defineComponent({
	name: 'DashboardVorstandView',
	components: {
		TheBox,
		TheButton,
		TheTodoList,
		TheTable,
		LastActivitiesTable,
		ComplaintsTable,
		CasesTable,
		DashFilter,
		Plus,
		SortIcon,
		TableSortDrawer,
	},
	setup() {
		const store = useStore();
		const isMobile = computed(() => store.state.isMobile);
		return {
			store,
			user: authUser,
			isMobile,
		};
	},
	data() {
		return {
			userActivities: [] as ActivityResponse[],
			activitiesLoading: false,
			filteredComplaints: [] as ComplaintResponse[],
			complaintsTotalCount: 0,
			complaintsTableLoading: false,
			filteredCases: [] as CaseResponse[],
			casesTotalCount: 0,
			caseTableLoading: false,
			tablePage: 0,
			itemsPerPage: 14,
			mainfilters: [
				{
					key: 'case',
					label: 'Fälle',
					active: true,
					subfilters: [
						{ key: CaseFilters.ExaminationSmallSenate, label: 'im kleinen Senat', active: false, count: 0 },
						{ key: CaseFilters.Statements, label: 'Stellungnahme angefordert', active: false, count: 0 },
						{ key: CaseFilters.ExaminationWerberat, label: 'im Werberat', active: false, count: 0 },
					],
				},
				{
					key: 'decision',
					label: 'Entscheidungen',
					active: false,
					subfilters: [
						{ key: CaseState.DecisionStop, label: 'STOPP', active: false, count: 0 },
						{ key: CaseState.DecisionSensitization, label: 'Sensibilisierung', active: false, count: 0 },
						{ key: CaseState.DecisionNoReason, label: 'Kein Grund', active: false, count: 0 },
					],
				},
			] as FilterItem[],
			complaintFilters: [] as any[],
			casesFilters: [] as any[],
			openTableSortDrawer: false,
			tableSort: { prop: 'voteEndDate', order: 'ascending' } as TableSort, // Current sort option
		};
	},
	computed: {
		texts() {
			return texts;
		},
		mainfilter_active(): FilterItem {
			return this.mainfilters.find((m) => m.active) || this.mainfilters[0];
		},
	},
	methods: {
		async loadCases(filters) {
			this.caseTableLoading = true;
			let dashboardSearch: SearchCaseRequest = {
				searchTerm: null,
				fromDate: null,
				toDate: null,
				states: [],
				isPublished: null,
				category: null,
				medium: null,
				reach: null,
				sector: null,
			};
			filters.forEach((f) => {
				if (f === CaseFilters.ExaminationWerberat) {
					dashboardSearch.states!.push(CaseState.ExaminationWerberat);
					dashboardSearch.states!.push(CaseState.ExaminationWerberatRunning);
				} else if (f === CaseFilters.ExaminationSmallSenate) {
					dashboardSearch.states!.push(CaseState.ExaminationSmallSenate);
					dashboardSearch.states!.push(CaseState.ExaminationSmallSenateRunning);
				} else {
					dashboardSearch.states!.push(f);
				}
			});

			const caseResult = await searchCases(dashboardSearch, this.tablePage, 20, true);
			if (caseResult.success) {
				this.casesTotalCount = caseResult.data.totalCount;
				if (this.tablePage === 0) {
					this.filteredCases = caseResult.data.items;
				} else {
					this.filteredCases = [...this.filteredCases, ...caseResult.data.items];
				}

				this.mainfilters = this.mainfilters.map((m) => {
					if (m.key === 'case') {
						m.subfilters = m.subfilters.map((f) => {
							if (f.key === CaseFilters.Statements) {
								f.count = caseResult.data.counts?.[CaseFilters.Statements.toString()] ?? 0;
							} else if (f.key === CaseFilters.ExaminationSmallSenate) {
								f.count = caseResult.data.counts?.['ExaminationSmallSenateRunning'] ?? 0;
							} else if (f.key === CaseFilters.ExaminationWerberat) {
								f.count = caseResult.data.counts?.['ExaminationWerberatRunning'] ?? 0;
							}
							return f;
						});
					} else if (m.key === 'decision') {
						m.subfilters = m.subfilters.map((f) => {
							if (f.key === CaseState.DecisionStop) {
								f.count = caseResult.data.counts?.[CaseState.DecisionStop.toString()] ?? 0;
							} else if (f.key === CaseState.DecisionSensitization) {
								f.count = caseResult.data.counts?.[CaseState.DecisionSensitization.toString()] ?? 0;
							} else if (f.key === CaseState.DecisionNoReason) {
								f.count = caseResult.data.counts?.[CaseState.DecisionNoReason.toString()] ?? 0;
							}
							return f;
						});
					}
					return m;
				});
			} else {
				this.filteredCases = [];
				this.casesTotalCount = 0;
			}

			this.caseTableLoading = false;
		},
		async loadNextPage() {
			this.tablePage++;
			const filters = this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key);
			this.loadCases(filters);
		},
		toggleMainFilter(filter) {
			this.tablePage = 0;
			this.mainfilters = this.mainfilters.map((m) => {
				// set all mainfilters to false except current one
				return {
					...m,
					active: m.key === filter ? true : false,
				};
			});

			const filters = this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key);
			this.loadCases(filters);
		},
		toggleSubFilter(filter) {
			this.tablePage = 0;
			const findSub = this.mainfilter_active.subfilters.find((f) => f.key === filter);
			if (findSub) {
				findSub.active = !findSub.active;
			}
			const filters = this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key);
			this.loadCases(filters);
		},
	},
	mounted() {
		this.loadCases([]);
	},
});
</script>
